// locale-unextracted-ignore-file

import { getQueryParam } from '@mi-front/core';
import Add from '@mui/icons-material/Add';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Remove from '@mui/icons-material/Remove';
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

const LABEL = 'Tipografía';
const FONT_SIZE_LABEL = 'Tamaño base';

/** Base font size scale as percentage (e.g. 100 = 100%). */
const FONT_SIZE_SCALE_MIN = 80;
const FONT_SIZE_SCALE_MAX = 140;
const FONT_SIZE_SCALE_STEP = 5;
const FONT_SIZE_SCALE_DEFAULT = 100;

type FontOption = { value: string; label: string };

const DEFAULT_FONT_OPTION: FontOption = { value: '', label: 'Rubik (por defecto)' };

const FONT_OPTIONS: FontOption[] = [
  DEFAULT_FONT_OPTION,
  { value: 'Albert+Sans', label: 'Albert Sans' },
  { value: 'Archivo', label: 'Archivo' },
  { value: 'Asap', label: 'Asap' },
  { value: 'Assistant', label: 'Assistant' },
  { value: 'Barlow', label: 'Barlow' },
  { value: 'Be+Vietnam+Pro', label: 'Be Vietnam Pro' },
  { value: 'Cabin', label: 'Cabin' },
  { value: 'Chivo', label: 'Chivo' },
  { value: 'DM+Sans', label: 'DM Sans' },
  { value: 'Encode+Sans', label: 'Encode Sans' },
  { value: 'Exo+2', label: 'Exo 2' },
  { value: 'Figtree', label: 'Figtree' },
  { value: 'Fira+Sans', label: 'Fira Sans' },
  { value: 'Geist', label: 'Geist' },
  { value: 'Golos+Text', label: 'Golos Text' },
  { value: 'Hanken+Grotesk', label: 'Hanken Grotesk' },
  { value: 'Heebo', label: 'Heebo' },
  { value: 'IBM+Plex+Sans', label: 'IBM Plex Sans' },
  { value: 'Instrument+Sans', label: 'Instrument Sans' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Karla', label: 'Karla' },
  { value: 'Libre+Franklin', label: 'Libre Franklin' },
  { value: 'Manrope', label: 'Manrope' },
  { value: 'Mulish', label: 'Mulish' },
  { value: 'Noto+Sans', label: 'Noto Sans' },
  { value: 'Nunito+Sans', label: 'Nunito Sans' },
  { value: 'Onest', label: 'Onest' },
  { value: 'Overpass', label: 'Overpass' },
  { value: 'Plus+Jakarta+Sans', label: 'Plus Jakarta Sans' },
  { value: 'PT+Sans', label: 'PT Sans' },
  { value: 'Public+Sans', label: 'Public Sans' },
  { value: 'Readex+Pro', label: 'Readex Pro' },
  { value: 'Red+Hat+Text', label: 'Red Hat Text' },
  { value: 'Roboto+Flex', label: 'Roboto Flex' },
  { value: 'Saira', label: 'Saira' },
  { value: 'Schibsted+Grotesque', label: 'Schibsted Grotesque' },
  { value: 'Source+Sans+3', label: 'Source Sans 3' },
  { value: 'Space+Grotesk', label: 'Space Grotesk' },
  { value: 'Spline+Sans', label: 'Spline Sans' },
  { value: 'Work+Sans', label: 'Work Sans' },
];

const DYNAMIC_FONT_LINK_ID = 'dynamic-font-switcher-link';
const FONT_PRELOAD_LINK_ID = 'font-switcher-preload-all';

/**
 * Injects a stylesheet that loads all fonts from the list so dropdown options can render in their font.
 */
function ensureFontsPreloaded() {
  const optionsWithFont = FONT_OPTIONS.filter((o) => o.value);
  if (optionsWithFont.length === 0) return;
  const familyParams = optionsWithFont.map((o) => `family=${o.value}:wght@400`).join('&');
  const url = `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;
  let link = document.getElementById(FONT_PRELOAD_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = FONT_PRELOAD_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = url;
}

/**
 * Injects a Google Fonts stylesheet and sets the app font CSS variable.
 * Empty value restores the default (Rubik) from the stylesheet.
 */
function applyFont(fontValue: string) {
  if (!fontValue) {
    document.documentElement.style.removeProperty('--font-family-sans-serif');
    const link = document.getElementById(DYNAMIC_FONT_LINK_ID);
    if (link) link.remove();
    return;
  }

  const fontName = fontValue.replace(/\+/g, ' ');
  const url = `https://fonts.googleapis.com/css2?family=${fontValue}:wght@300;400;500;600;700&display=swap`;

  let link = document.getElementById(DYNAMIC_FONT_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = DYNAMIC_FONT_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = url;

  document.documentElement.style.setProperty(
    '--font-family-sans-serif',
    `"${fontName}", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
  );
}

/**
 * Applies base font size scale to the document root.
 * Scale is a percentage (e.g. 100 = 100%). Removes the variable if default.
 */
function applyFontSizeScale(scalePercent: number) {
  if (scalePercent === FONT_SIZE_SCALE_DEFAULT) {
    document.documentElement.style.removeProperty('--font-size-scale');
    return;
  }
  document.documentElement.style.setProperty('--font-size-scale', String(scalePercent / 100));
}

export function FontSwitcher() {
  const [font, setFont] = useState<string>();
  const [fontSizeScale, setFontSizeScale] = useState<number>(FONT_SIZE_SCALE_DEFAULT);

  useEffect(() => {
    if (font) {
      applyFont(font);
    }
  }, [font]);

  useEffect(() => {
    applyFontSizeScale(fontSizeScale);
  }, [fontSizeScale]);

  useEffect(() => {
    ensureFontsPreloaded();
  }, []);

  const decreaseFontSize = useCallback(() => {
    setFontSizeScale((prev) => Math.max(FONT_SIZE_SCALE_MIN, prev - FONT_SIZE_SCALE_STEP));
  }, []);

  const increaseFontSize = useCallback(() => {
    setFontSizeScale((prev) => Math.min(FONT_SIZE_SCALE_MAX, prev + FONT_SIZE_SCALE_STEP));
  }, []);

  if (getQueryParam('font_preview') == null) {
    return null;
  }

  const selectedOption = FONT_OPTIONS.find((o) => o.value === font) ?? FONT_OPTIONS[0];
  const currentIndex = Math.max(
    0,
    FONT_OPTIONS.findIndex((o) => o.value === selectedOption.value),
  );

  const goToPrev = useCallback(() => {
    const prevIndex = currentIndex <= 0 ? FONT_OPTIONS.length - 1 : currentIndex - 1;
    setFont(FONT_OPTIONS[prevIndex].value);
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    const nextIndex = currentIndex >= FONT_OPTIONS.length - 1 ? 0 : currentIndex + 1;
    setFont(FONT_OPTIONS[nextIndex].value);
  }, [currentIndex]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        backgroundColor: 'background.paper',
        padding: 1.5,
        borderRadius: 1,
        boxShadow: 3,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="font-switcher-label">{LABEL}</InputLabel>
          <Select
            labelId="font-switcher-label"
            value={selectedOption.value}
            label={LABEL}
            onChange={(e) => setFont(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 320 },
              },
            }}
          >
            {FONT_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'default'} value={option.value}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: option.value ? `"${option.label}", sans-serif` : undefined,
                  }}
                >
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={goToPrev} aria-label="Font anterior">
          <KeyboardArrowUp />
        </IconButton>
        <IconButton onClick={goToNext} aria-label="Font siguiente">
          <KeyboardArrowDown />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ typography: 'body2', minWidth: 80 }}>
          {FONT_SIZE_LABEL}: {fontSizeScale}%
        </Box>
        <IconButton onClick={decreaseFontSize} aria-label="Reducir tamaño">
          <Remove />
        </IconButton>
        <IconButton onClick={increaseFontSize} aria-label="Aumentar tamaño">
          <Add />
        </IconButton>
      </Box>
    </Box>
  );
}
